import { Component } from '@theme/component';
import VariantPicker from '@theme/variant-picker';

/** @typedef {'uk' | 'us'} SizeSystem */

/** Also read by the inline script in snippets/scripts.liquid before the page renders. */
const STORAGE_KEY = 'size-system';
const SIZE_SYSTEM_CHANGE_EVENT = 'size-system:change';

/**
 * @param {unknown} value
 * @returns {value is SizeSystem}
 */
function isSizeSystem(value) {
  return value === 'uk' || value === 'us';
}

/**
 * @returns {SizeSystem | null}
 */
function getStoredSizeSystem() {
  try {
    const storedSizeSystem = localStorage.getItem(STORAGE_KEY);
    return isSizeSystem(storedSizeSystem) ? storedSizeSystem : null;
  } catch {
    return null;
  }
}

/**
 * Shows option values such as "UK 8 / US 4" in one size system across the page.
 * @param {SizeSystem} sizeSystem
 */
function applySizeSystem(sizeSystem) {
  const root = document.documentElement;
  if (root.dataset.sizeSystem === sizeSystem) return;

  root.dataset.sizeSystem = sizeSystem;

  // Labels change width with the size system, so the selected pill has to be measured again.
  for (const variantPicker of document.querySelectorAll('variant-picker')) {
    if (variantPicker instanceof VariantPicker) variantPicker.updateVariantPickerCss();
  }

  document.dispatchEvent(new CustomEvent(SIZE_SYSTEM_CHANGE_EVENT));
}

/**
 * Buttons that switch size option values between their UK and US sizes.
 */
class SizeSystemToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(SIZE_SYSTEM_CHANGE_EVENT, this.#updatePressedState);
    this.#updatePressedState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(SIZE_SYSTEM_CHANGE_EVENT, this.#updatePressedState);
  }

  /**
   * @param {string} sizeSystem - The size system to show, either 'uk' or 'us'.
   */
  selectSystem(sizeSystem) {
    if (!isSizeSystem(sizeSystem)) return;

    try {
      localStorage.setItem(STORAGE_KEY, sizeSystem);
    } catch {
      // Without storage (e.g. blocked cookies) the choice only lasts until the next page load.
    }

    applySizeSystem(sizeSystem);
  }

  #updatePressedState = () => {
    const { sizeSystem } = document.documentElement.dataset;

    for (const button of this.querySelectorAll('button[data-size-system]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.setAttribute('aria-pressed', String(button.dataset.sizeSystem === sizeSystem));
    }
  };
}

if (!customElements.get('size-system-toggle')) {
  customElements.define('size-system-toggle', SizeSystemToggle);
}

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY && isSizeSystem(event.newValue)) applySizeSystem(event.newValue);
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  const storedSizeSystem = getStoredSizeSystem();
  if (storedSizeSystem) applySizeSystem(storedSizeSystem);
});
